import { IconButton, TextField } from "@mui/material";
import MinusIcon from "assets/icons/MinusIcon";
import PlusIcon from "assets/icons/PlusIcon";
import { parseLocaleNumber } from "components/openingBalance/helpers";
import { formatNumber, formatPrice } from "helpers";
import { useEffect, useRef, useState } from "react";

export default function BasicTextInput(props: BasicTextInputProps) {
  const { val, onChange, suffix, prefix, type, style, min, max, label, actionSection } = props;
  const { disabled, required, fullWidth, textArea, rows, placeHolder, onBlur, onWheel } = props;
  const { TextFieldProps, autoComplete } = props;
  const [value, setValue] = useState<string | number>(0);
  const ref = useRef<any>(null);

  function is_numeric_char(c: string) {
    return /\d/.test(c);
  }

  useEffect(() => {
    const v = getValue(val ? `${val}` : type === "number" ? "0" : "");
    if (v === undefined) return;
    setValue(v);
  }, [type, val]);

  function getValue(val: string | number) {
    if (type === "price" || type === "quantity") {
      return formatPrice(val);
    } else if (type && ["number", "number-native"].includes(type)) {
      const v = parseFloat(formatNumber(val));
      if (min !== undefined && v < min) return min;
      if (max !== undefined && v > max) return max;
      return v;
    } else {
      return val;
    }
  }

  function handleChange(v: string | number) {
    let val = getValue(v);
    if ((type === "price" || type === "quantity") && typeof v === "string") {
      if (v.includes("+")) {
        const correctVal = val += "000"
        val = type === "quantity" ? formatPrice(val) : parseLocaleNumber(formatPrice(val));
        setValue(val);
        onChange && onChange(parseFloat(type === "price" || type === "quantity" ? correctVal.toString().replace(/\,/g, "") : correctVal));
        return
      }
      if (v.length > 0 && !is_numeric_char(v)) return;
      if (v.substring(v.length - 1, v.length) === ".") {
        setValue(v);
        return;
      }
      if (v.length === 0) {
        setValue(0);
        onChange && onChange(0);
        return;
      }
      val = type === "quantity" ? formatPrice(v) : parseLocaleNumber(formatPrice(v));
    }
    if (val === undefined) return;
    setValue(val);
    val !== undefined && onChange && onChange(type === "quantity" ? val.toString().replace(/\,/g, "") : val);
  }

  function increment(operator: string) {
    let v = value;
    if (v === undefined) return;
    if (typeof v === "string") v = parseInt(v);
    if (min !== undefined && v <= min && operator === "minus") return;
    if (max !== undefined && v >= max && operator === "plus") return;
    const res = operator === "plus" ? v + 1 : v - 1;
    setValue(res);
    onChange && onChange(res);
  }

  return (
    <div style={{ ...(fullWidth && { width: "100%" }) }}>
      {(label || actionSection) && (
        <div
          style={{
            fontSize: 14,
            fontWeight: 400,
            height: 32,
            padding: "0px 8px 12px 0px",
            color: "#33383D",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>
            {label}
            {required && "*"}
          </span>
          {actionSection}
        </div>
      )}
      {textArea ? (
        <textarea
          placeholder={placeHolder}
          className="w-full p-2 border rounded-md outline-none border-slate-300"
          rows={3}
          disabled={disabled}
          onChange={(e) => handleChange(e.target.value)}
          value={value}
        />
      ) : (
        <div
          className="flex items-center h-10 rounded-md cursor-text"
          style={{
            border: "1px solid #cbd5e1",
            pointerEvents: disabled ? "none" : "auto",
            padding: "0px 12px",
            ...(style || {}),
            ...(disabled && { backgroundColor: "#f2f5fc" }),
          }}
          onClick={() => ref.current && ref.current.focus()}
        >
          <div className="flex items-center w-full gap-2 whitespace-nowrap" style={{ direction: "rtl" }}>
            {suffix}
            {type === "number" && (
              <IconButton style={{ width: 20, height: 20 }} onClick={() => increment("minus")}>
                <MinusIcon scale={3} />
              </IconButton>
            )}
            <TextField
              inputRef={ref}
              fullWidth
              size="small"
              variant="standard"
              disabled={disabled}
              placeholder={placeHolder}
              onBlur={onBlur}
              autoComplete={autoComplete || "off"}
              style={{
                color: "#3D3D3D",
                border: "none",
                minWidth: 60,
              }}
              value={
                (type === "price" || type === "quantity") && typeof value === "number" ? Intl.NumberFormat().format(value) : value
              }

              onChange={(e) => {  handleChange(e.target.value) }}
              onWheel={onWheel}
              onWheelCapture={onWheel}
              InputProps={{ disableUnderline: true }}
              inputProps={{
                min,
                max,
                onClick: (e) => e.currentTarget.select(),
                style: {
                  padding: 0,
                  fontSize: 14,
                  fontWeight: 400,
                  ...(type && ["number-native", "number"].includes(type)
                    ? { direction: "ltr", textAlign: "center" }
                    : type && ["price"].includes(type)
                      ? { direction: "ltr", textAlign: "end" }
                      : {}),
                  ...props.inputStyle,
                },
              }}
              {...TextFieldProps}
              {...(type && ["number", "number-native"].includes(type) && { type: "number" })}
              {...(type && ["email", "password"].includes(type) && { type })}
              className={`${(type && ["number"].includes(type)) || props.arrowHidden ? "hidden-arrow" : ""}`}
            />
            {type === "number" && (
              <IconButton style={{ width: 20, height: 20 }} onClick={() => increment("plus")}>
                <PlusIcon scale={3} />
              </IconButton>
            )}
            {prefix}
          </div>
        </div>
      )}
      {props.helperText && <div className="py-2 text-xs text-gray-400">{props.helperText}</div>}
    </div>
  );
}
